import './signUpPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import trim from 'lodash/trim';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import logo from '../../assets/logo-default.svg';
import { PhoneNumberInput } from '../../components/PhoneNumberInput/PhoneNumberInput';
import {
  emailValidator,
  nameValidator,
} from '../../components/utils/inputValidators';
import useApiStore from '../../store/apiStore/useApiStore';
import { FeatureFlag } from '../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../store/featureStore/hooks/useFeatureFlag';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignUpStore from '../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { UserExistsExceptionCode } from '../../store/userStore/enums/UserExistsExceptionCode';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();

  const { t } = useTranslation();

  const { deliverPin, deliverPinFailedMessage } = usePinStore();

  const { userExists } = useUserStore();

  const { firstName, lastName, primaryPhoneNumber, primaryEmailAddress } =
    useSignUpStore();

  const form = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: firstName,
      lastNameValue: lastName,
      primaryEmailAddressValue: primaryEmailAddress,
      primaryPhoneNumberValue: primaryPhoneNumber || '',
    },
    delayError: 200,
    shouldFocusError: false,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const subtitle = useFeatureFlag(FeatureFlag.ENABLE_REFANDI_SIGN_UP_COPY)
    ? t('SignUpPage.refandiSubtitle')
    : t('SignUpPage.subtitle');

  const firstNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: emailValidator,
      message: t('validation.emailAddress.invalid'),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
    } = values;

    useSignUpStore.setState({
      firstName: firstNameValue,
      lastName: lastNameValue,
      primaryPhoneNumber: primaryPhoneNumberValue,
      primaryEmailAddress: primaryEmailAddressValue,
    });

    await userExists(primaryPhoneNumberValue, primaryEmailAddressValue);

    const { userExistsException } = useUserStore.getState();

    if (userExistsException === UserExistsExceptionCode.BOTH_NOT_FOUND) {
      await deliverPin({
        deliverPinLocation: DeliverPinLocation.SIGN_IN,
        phone: primaryPhoneNumberValue,
        purpose: AuthType.ACCOUNT_REGISTRATION_USING_PIN,
      });

      const { deliverPinSuccess } = usePinStore.getState();

      if (deliverPinSuccess) {
        navigate('/sign-up/pin-verify');
      }
    } else {
      navigate('/sign-up/returning-user');
    }
  };

  return (
    <div className="sign-up-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.signUp.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <Link
              data-testid={locator.pages.signUp.header.signInLink}
              to="/sign-in"
            >
              {t('SignUpPage.signin')}
            </Link>
          </header>

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form
              data-testid={locator.pages.signUp.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signUp.form.h1}>
                  {t('SignUpPage.title')}
                </h1>
                <h2 data-testid={locator.pages.signUp.form.h2}>{subtitle}</h2>

                {deliverPinFailedMessage && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.signUp.alert.error}
                      icon={false}
                      severity="error"
                    >
                      {deliverPinFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('firstNameValue', firstNameValidation)}
                        error={!isEmpty(errors.firstNameValue)}
                        label={t('SignUpPage.firstName')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.signUp.form.firstNameInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.firstNameValue) && (
                        <span
                          data-testid={
                            locator.pages.signUp.form.firstNameInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.firstNameValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('lastNameValue', lastNameValidation)}
                        error={!isEmpty(errors.lastNameValue)}
                        label={t('SignUpPage.lastName')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.signUp.form.lastNameInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.lastNameValue) && (
                        <span
                          data-testid={
                            locator.pages.signUp.form.lastNameInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.lastNameValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <PhoneNumberInput
                      control={control}
                      errorTestId={locator.pages.signUp.form.phoneInputError}
                      inputLabelKey={'SignInPage.phoneNumber'}
                      name="primaryPhoneNumberValue"
                      slotProps={{
                        htmlInput: {
                          'data-testid': locator.pages.signUp.form.phoneInput,
                        },
                      }}
                    />
                  </div>
                  <div>
                    <TextField
                      {...register(
                        'primaryEmailAddressValue',
                        emailAddressValidation,
                      )}
                      error={!isEmpty(errors.primaryEmailAddressValue)}
                      label={t('SignUpPage.email')}
                      slotProps={{
                        htmlInput: {
                          'data-testid': locator.pages.signUp.form.emailInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.primaryEmailAddressValue) && (
                      <span
                        data-testid={locator.pages.signUp.form.emailInputError}
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.primaryEmailAddressValue.message)}
                      </span>
                    )}
                  </div>
                </div>

                <Button
                  color="primary"
                  data-testid={locator.pages.signUp.form.submitButton}
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('SignUpPage.getStarted')}
                </Button>

                <p className="form-terms-privacy">
                  <Trans
                    components={{
                      terms: (
                        <a
                          data-testid={locator.pages.signUp.form.termsLink}
                          href="https://www.thesavingsgroup.com/terms-of-use/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                      privacy: (
                        <a
                          data-testid={locator.pages.signUp.form.privacyLink}
                          href="https://www.thesavingsgroup.com/privacy-policy/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey={'SignUpPage.termsPrivacy'}
                  />
                </p>
              </div>
              <footer data-testid={locator.pages.signUp.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignUpPage';
